import { ParsedUrlQuery } from "querystring"
import { useEffect, useRef, type RefObject } from "react"
import { Session } from "@/types/auth"
import { UpdateUserProperties, UpdateUserPropertiesType } from "@/types/user"
import isEqual from "lodash/isEqual"

import { getUtmParams } from "@/lib/analytics/amplitude-utils"
import { UserApi } from "@/lib/user-api"
import { present } from "@/lib/utils/presence"

interface UseUtmTrackingProps {
  session?: Session
  query: ParsedUrlQuery
  userApi: UserApi
}

async function syncUtmProperties(
  session: Session | undefined,
  query: ParsedUrlQuery,
  userApi: UserApi,
  prevUtms: RefObject<UpdateUserProperties | null>
) {
  const utmProperties = getUtmParams(query)
  const newUtms = {
    type: "utm-props" as UpdateUserPropertiesType,
    utm_source: utmProperties.utmSource,
    utm_medium: utmProperties.utmMedium,
    utm_campaign: utmProperties.utmCampaign,
    utm_term: utmProperties.utmTerm,
    utm_content: utmProperties.utmContent,
    epik: utmProperties.epik,
  }
  const hasTrackingValues = [
    newUtms.utm_source,
    newUtms.utm_medium,
    newUtms.utm_campaign,
    newUtms.utm_term,
    newUtms.utm_content,
    newUtms.epik,
  ].some(present)
  if (session?.user && hasTrackingValues) {
    if (!isEqual(newUtms, prevUtms.current)) {
      prevUtms.current = newUtms
      try {
        await userApi.updateUserProperties(newUtms)
      } catch {
        // Prevent unhandled promise rejection from fire-and-forget call
      }
    }
  }
}

/**
 * Hook to track and update UTM parameters via the user API
 * Shared between WebApp and MinimalApp
 */
export function useUtmTracking({
  session,
  query,
  userApi,
}: UseUtmTrackingProps) {
  const prevUtms = useRef<UpdateUserProperties | null>(null)

  useEffect(() => {
    void syncUtmProperties(session, query, userApi, prevUtms)
  }, [session, query, userApi])
}
